'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { X, Download, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useTargetAttachments } from '../hooks/queries/useAttachments';
import { useWorkspaceScope } from '../hooks/useWorkspaceScope';
import { cn } from '../lib/cn';
import { BaseModal } from './BaseModal';
import { AttachmentPreviewContent } from './AttachmentPreviewContent';
import type { Attachment, AttachmentTargetType } from '../types/attachment';

type AttachmentBrowserModalProps = {
  targetType: AttachmentTargetType;
  targetId: string;
  isOpen: boolean;
  onClose: () => void;
  initialAttachmentId?: string;
};

const formatBytes = (value: number) => {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
};

export function AttachmentBrowserModal({
  targetType,
  targetId,
  isOpen,
  onClose,
  initialAttachmentId,
}: AttachmentBrowserModalProps) {
  const t = useTranslations('components.attachment');
  const tCommon = useTranslations('common');
  const { token } = useWorkspaceScope();
  const attachmentsQuery = useTargetAttachments(token, targetType, isOpen ? targetId : null);
  const attachments = useMemo(() => attachmentsQuery.data ?? [], [attachmentsQuery.data]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // 모달이 열릴 때 초기 선택값 설정
  useEffect(() => {
    if (!isOpen || attachments.length === 0) {
      setSelectedId(null);
      return;
    }
    const matched = initialAttachmentId && attachments.find((a) => a.id === initialAttachmentId);
    setSelectedId((current) => current ?? (matched ? matched.id : attachments[0].id));
  }, [isOpen, attachments, initialAttachmentId]);

  // 모달 닫힐 때 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setSelectedId(null);
      setDownloadUrl(null);
    }
  }, [isOpen]);

  const handleDownloadUrlChange = useCallback((url: string | null) => {
    setDownloadUrl(url);
  }, []);

  if (!isOpen) return null;

  const selected: Attachment | null = attachments.find((a) => a.id === selectedId) ?? null;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      variant="bottomSheet"
      size="5xl"
      className="flex h-[86dvh] flex-col overflow-hidden !p-0 sm:h-[86vh]"
    >
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-base-300 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            className="btn btn-ghost btn-sm btn-circle shrink-0"
            onClick={() => setIsSidebarOpen((current) => !current)}
            aria-label={isSidebarOpen ? t('hideSidebar') : t('showSidebar')}
            aria-expanded={isSidebarOpen}
            title={isSidebarOpen ? t('hideSidebar') : t('showSidebar')}
          >
            {isSidebarOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
          </button>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{selected?.originalName ?? t('title')}</p>
            {selected ? (
              <p className="text-xs text-[var(--color-fg-subtle)]">
                {selected.mimeType} · {formatBytes(selected.size)}
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {downloadUrl && selected ? (
            <a
              className="btn btn-ghost btn-sm btn-circle"
              href={downloadUrl}
              download={selected.originalName}
              aria-label={t('download')}
              title={t('download')}
            >
              <Download className="h-4 w-4" />
            </a>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost btn-sm btn-circle"
            onClick={onClose}
            aria-label={tCommon('actions.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Body: sidebar + main */}
      <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
        {/* Sidebar */}
        <aside
          className={cn(
            'shrink-0 overflow-x-auto overflow-y-auto border-b border-base-300 bg-base-200/50 sm:border-b-0 sm:border-r',
            isSidebarOpen ? 'sm:w-56' : 'hidden',
          )}
        >
          <ul className="flex flex-row gap-1 p-2 sm:flex-col">
            {attachments.map((attachment) => {
              const isSelected = attachment.id === selectedId;
              return (
                <li key={attachment.id} className="shrink-0 sm:shrink">
                  <button
                    type="button"
                    onClick={() => setSelectedId(attachment.id)}
                    className={cn(
                      'w-full rounded-md px-2 py-1.5 text-left text-xs transition-colors',
                      isSelected ? 'bg-primary/10 text-primary' : 'text-[var(--color-fg-muted)] hover:bg-base-200',
                    )}
                  >
                    <div className="truncate font-medium">{attachment.originalName}</div>
                    <div className="text-[10px] text-[var(--color-fg-subtle)]">{formatBytes(attachment.size)}</div>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Main preview area */}
        <div className="min-h-0 flex-1 overflow-hidden">
          <AttachmentPreviewContent token={token} attachment={selected} onDownloadUrlChange={handleDownloadUrlChange} />
        </div>
      </div>
    </BaseModal>
  );
}
